/**
 * Multi-format export for device data.
 * Supports CSV, Excel (.xlsx), and JSON.
 */
import { CSV_SEPARATOR } from "@/utils/Utils";
import {
  DownloadFileType,
  getCsvFormatLabel,
  getFileTypeIcon,
  UNKNOWN_FILE_TYPE_ICON,
} from "@/utils/FileTypeIcons";

export enum ExportFormat {
  CSV = "CSV",
  EXCEL = "EXCEL",
  JSON = "JSON",
}

/**
 * Icon for one option of a format picker, so every export screen names a file type the same way.
 *
 * A `switch` rather than a record: a format added to the enum then shows the neutral file icon
 * instead of nothing at all.
 */
export function getExportFormatIcon(format: ExportFormat): string {
  switch (format) {
    case ExportFormat.CSV:
      return getFileTypeIcon(DownloadFileType.CSV);
    case ExportFormat.EXCEL:
      return getFileTypeIcon(DownloadFileType.EXCEL);
    case ExportFormat.JSON:
      return getFileTypeIcon(DownloadFileType.JSON);
    default:
      return UNKNOWN_FILE_TYPE_ICON;
  }
}

/** Label of the CSV option, stating the separator these exports actually use. */
export function getCsvExportFormatLabel(): string {
  return getCsvFormatLabel(CSV_SEPARATOR);
}

function downloadBlob(blob: Blob, fullFileName: string) {
  const link = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  link.setAttribute("download", fullFileName);
  link.click();
  URL.revokeObjectURL(objectUrl);
}

function parseRows(headers: string[], dataRows: string[]): string[][] {
  return dataRows.map((row) => {
    const values = row.split(CSV_SEPARATOR);
    return headers.map((_, i) => (values[i] ?? "").trim());
  });
}

// --- CSV ---

function downloadCsv(headers: string[], dataRows: string[], fileName: string) {
  const csvContent = [headers.join(CSV_SEPARATOR), ...dataRows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, `${fileName}.csv`);
}

// --- Excel ---

async function downloadExcel(headers: string[], dataRows: string[], fileName: string) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Devices");

  // Header row
  const headerRow = sheet.addRow(headers);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDDDFDF" },
    };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FFC8CACA" } },
    };
  });

  // Data rows
  const parsed = parseRows(headers, dataRows);
  for (const row of parsed) {
    sheet.addRow(row);
  }

  // Auto-width columns
  sheet.columns.forEach((col) => {
    let maxLen = 10;
    col.eachCell?.({ includeEmpty: true }, (cell) => {
      const len = cell.value ? String(cell.value).length : 0;
      if (len > maxLen) maxLen = len;
    });
    col.width = Math.min(maxLen + 2, 40);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(blob, `${fileName}.xlsx`);
}

// --- Excel (multi-sheet) ---

/** A single value in a multi-sheet Excel row. Dates/numbers keep their type so Excel sorts them correctly. */
export type ExcelCellValue = string | number | Date | null | undefined;

/** Column definition for a multi-sheet Excel export. `numFmt` applies an Excel number/date format to the column. */
export interface ExcelColumnSpec {
  header: string;
  width?: number;
  numFmt?: string;
}

/** One worksheet in a multi-sheet Excel export. `rows` are ordered to match `columns`. */
export interface ExcelSheetSpec {
  name: string;
  columns: ExcelColumnSpec[];
  rows: ExcelCellValue[][];
}

// Multi-sheet styling (ARGB, as ExcelJS requires). Classic Excel "blue table" look.
const EXCEL_HEADER_FILL_ARGB = "FF305496"; // dark blue header background
const EXCEL_HEADER_FONT_ARGB = "FFFFFFFF"; // white bold header text
const EXCEL_ZEBRA_FILL_ARGB = "FFD9E1F2"; // light blue banding for alternate rows
const EXCEL_HEADER_BORDER_ARGB = "FFC8CACA";

/**
 * Downloads a multi-sheet Excel workbook (one worksheet per {@link ExcelSheetSpec}).
 *
 * Unlike {@link downloadDeviceExport}/`downloadExcel` (single "Devices" sheet, string rows),
 * this writes typed cells: a `Date` column is written as a real date with the given `numFmt`
 * and numeric cells stay numeric, so the file opens and sorts cleanly in Excel. Header styling
 * and auto-width match the single-sheet exporter.
 */
export async function downloadExcelMultiSheet(
  sheets: ExcelSheetSpec[],
  fileName: string
): Promise<void> {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();

  for (const sheetSpec of sheets) {
    const sheet = workbook.addWorksheet(sheetSpec.name);

    // Styled header row: white bold text on a dark-blue fill.
    const headerRow = sheet.addRow(sheetSpec.columns.map((col) => col.header));
    headerRow.font = { bold: true, color: { argb: EXCEL_HEADER_FONT_ARGB } };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: EXCEL_HEADER_FILL_ARGB },
      };
      cell.border = {
        bottom: { style: "thin", color: { argb: EXCEL_HEADER_BORDER_ARGB } },
      };
    });

    // Data rows (typed cells), with zebra banding on alternate rows.
    sheetSpec.rows.forEach((row, rowIndex) => {
      const dataRow = sheet.addRow(row);

      if (rowIndex % 2 === 1) {
        dataRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: EXCEL_ZEBRA_FILL_ARGB },
          };
        });
      }
    });

    // Per-column number/date format + width
    sheetSpec.columns.forEach((colSpec, columnIndex) => {
      const col = sheet.getColumn(columnIndex + 1);

      if (colSpec.numFmt) {
        col.numFmt = colSpec.numFmt;
      }

      if (colSpec.width) {
        col.width = colSpec.width;
        return;
      }

      let maxLen = 10;
      col.eachCell?.({ includeEmpty: true }, (cell) => {
        const len = cell.value ? String(cell.value).length : 0;
        if (len > maxLen) maxLen = len;
      });
      col.width = Math.min(maxLen + 2, 40);
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(blob, `${fileName}.xlsx`);
}

// --- JSON ---

function downloadJson(headers: string[], dataRows: string[], fileName: string) {
  const parsed = parseRows(headers, dataRows);
  const objects = parsed.map((row) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = row[i];
    });
    return obj;
  });

  const json = JSON.stringify(objects, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8;" });
  downloadBlob(blob, `${fileName}.json`);
}

// --- Main export function ---

/**
 * Downloads device data in the given format.
 *
 * @param headers - Localized column headers.
 * @param dataRows - Semicolon-separated data rows (without header row).
 * @param fileName - Base file name (without extension).
 */
export async function downloadDeviceExport(
  headers: string[],
  dataRows: string[],
  fileName: string,
  format: ExportFormat,
): Promise<void> {
  switch (format) {
    case ExportFormat.CSV:
      downloadCsv(headers, dataRows, fileName);
      break;
    case ExportFormat.EXCEL:
      await downloadExcel(headers, dataRows, fileName);
      break;
    case ExportFormat.JSON:
      downloadJson(headers, dataRows, fileName);
      break;
  }
}
